import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ShieldCheck, User, ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ mobile: "", otp: "", password: "", full_name: "", gender: "", profile_type: "self", email: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendOtp = async () => {
    if (!form.email || !form.email.includes("@")) return setErr("Enter a valid email — we'll send your verification code there.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/send-otp", { email: form.email, channel: "email" });
      if (data.user_exists) { setErr("An account with this email already exists. Please log in."); setBusy(false); return; }
      toast.success(data.message || "Verification code sent.");
      setStep(2);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (form.otp.length < 6) return setErr("Enter the 6-digit code sent to your email.");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/verify-otp", { email: form.email, otp: form.otp });
      toast.success("Email verified");
      setStep(3);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!/^\d{10}$/.test(form.mobile)) return setErr("Enter a valid 10-digit mobile number.");
    if (!form.full_name || !form.gender || form.password.length < 6) return setErr("Fill all fields. Password must be 6+ characters.");
    setBusy(true); setErr("");
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      const { data } = await api.post("/auth/register", { ...form, referral_code: ref || undefined });
      await loginWithToken(data.token, data.user);
      toast.success("Welcome to ManaJodi!");
      nav("/app/profile/edit");
    } catch (e) { setErr(apiError(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#3B0A12] text-[#FAF5F0]">
        <Link to="/" className="font-serif font-bold text-3xl">Mana<span className="text-[#C59B27]">Jodi</span></Link>
        <div>
          <h2 className="font-serif text-4xl font-bold leading-tight">Begin your journey<br />to your Jodi.</h2>
          <p className="text-[#E7CDCF] mt-4 max-w-sm">Join thousands finding meaningful, compatible matches with verified profiles and AI compatibility.</p>
        </div>
        <p className="text-xs text-[#D8B4B9]">Powered by Vijay Software Solutions Pvt Ltd</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/" className="font-serif font-bold text-3xl text-[#58111A]">Mana<span className="text-[#C59B27]">Jodi</span></Link></div>
          <div className="flex items-center gap-2 mb-2">
            {[1, 2, 3].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= s ? "bg-[#C59B27]" : "bg-[#E8DFD1]"}`} />)}
          </div>
          <div className="flex items-center gap-2 mb-6 text-[11px] font-medium">
            {["Email", "Verify", "Profile"].map((l, i) => (
              <span key={l} className={`flex-1 text-center ${step >= i + 1 ? "text-[#9E7B1B]" : "text-[#C9BCA8]"}`} data-testid={`register-step-label-${i + 1}`}>{l}</span>
            ))}
          </div>

          {err && <div className="mb-4 text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-2.5" data-testid="register-error">{err}</div>}

          {step === 1 && (
            <div className="animate-fadeup">
              <StepHead icon={Mail} title="Create your free profile" sub="Enter your email address to begin." />
              <label className="mj-label">Email Address</label>
              <input data-testid="register-email" type="email" className="mj-input" placeholder="Enter your Gmail or email address" value={form.email}
                onChange={(e) => set("email", e.target.value.trim())} onKeyDown={(e) => e.key === "Enter" && sendOtp()} />
              <p className="text-xs text-[#8A6D71] mt-1.5">We'll send a 6-digit verification code to this email.</p>
              <button data-testid="register-send-otp" onClick={sendOtp} disabled={busy} className="btn-primary w-full mt-5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send OTP <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-center text-sm text-[#605255] mt-6">Already a member? <Link to="/login" className="text-[#781826] font-semibold">Log in</Link></p>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fadeup">
              <StepHead icon={ShieldCheck} title="Verify your email" sub={`Enter the 6-digit code sent to ${form.email}.`} />
              <label className="mj-label">Verification code</label>
              <input data-testid="register-otp" className="mj-input tracking-widest text-lg" placeholder="6-digit OTP" value={form.otp}
                onChange={(e) => set("otp", e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && verifyOtp()} />
              <button data-testid="register-verify-otp" onClick={verifyOtp} disabled={busy} className="btn-primary w-full mt-5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
              </button>
              <div className="flex items-center justify-between mt-4">
                <button onClick={() => { setStep(1); setErr(""); }} className="text-sm text-[#605255] flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Change email</button>
                <button onClick={sendOtp} disabled={busy} className="text-sm text-[#781826] font-medium" data-testid="register-resend-otp">Resend code</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fadeup">
              <div className="mb-4 flex items-center gap-2 text-sm text-[#15803D] bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl px-4 py-2.5" data-testid="register-email-verified">
                <CheckCircle2 className="w-4 h-4" /> Email verified · {form.email}
              </div>
              <StepHead icon={User} title="Complete your profile" sub="Add your mobile number and set up your account." />
              <div className="space-y-3">
                <div>
                  <label className="mj-label">Mobile Number</label>
                  <input data-testid="register-mobile" className="mj-input" inputMode="numeric" placeholder="10-digit mobile number" value={form.mobile}
                    onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} />
                  <p className="text-xs text-[#8A6D71] mt-1.5">Used for account recovery. We won't send an OTP to this number.</p>
                </div>
                <div><label className="mj-label">Full Name</label><input data-testid="register-name" className="mj-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
                <div>
                  <label className="mj-label">This profile is for</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["self", "Myself"], ["parent", "Parent"], ["family", "Family"]].map(([v, l]) => (
                      <button key={v} onClick={() => set("profile_type", v)} className={`text-sm py-2 rounded-xl border ${form.profile_type === v ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`} data-testid={`ptype-${v}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mj-label">Gender</label>
                  <div className="grid grid-cols-2 gap-2">
                    {["Male", "Female"].map((g) => (
                      <button key={g} onClick={() => set("gender", g)} className={`text-sm py-2 rounded-xl border ${form.gender === g ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`} data-testid={`gender-${g}`}>{g}</button>
                    ))}
                  </div>
                </div>
                <div><label className="mj-label">Create Password</label><input data-testid="register-password" className="mj-input" type="password" placeholder="Min 6 characters" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
              </div>
              <button data-testid="register-submit" onClick={submit} disabled={busy} className="btn-primary w-full mt-5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create My Profile"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepHead({ icon: Icon, title, sub }) {
  return (
    <div className="mb-6">
      <div className="w-12 h-12 rounded-xl bg-[#FAF3E0] flex items-center justify-center text-[#781826] mb-4"><Icon className="w-6 h-6" /></div>
      <h1 className="font-serif text-2xl font-bold text-[#58111A]">{title}</h1>
      <p className="text-sm text-[#605255] mt-1">{sub}</p>
    </div>
  );
}
